import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Avatar,
  Box,
  Button,
  Card,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  TextField,
  Tooltip,
  Typography
} from '@material-ui/core';
import { makeStyles } from '@material-ui/styles';
import EditIcon from '@material-ui/icons/Edit';
import SaveIcon from '@material-ui/icons/Save';
import CancelIcon from '@material-ui/icons/Cancel';
import AddIcon from '@material-ui/icons/Add';
import DeleteIcon from '@material-ui/icons/Delete';
import LocationOnIcon from '@material-ui/icons/LocationOn';
import EmailIcon from '@material-ui/icons/Email';
import LinkedInIcon from '@material-ui/icons/LinkedIn';
import WorkIcon from '@material-ui/icons/Work';
import SchoolIcon from '@material-ui/icons/School';
import {
  addExperience,
  addSkill,
  candidateFetchedSelector,
  candidateLoadingSelector,
  candidateProfileSelector,
  deleteExperience,
  deleteSkill,
  experienceLoadingSelector,
  fetchCandidateProfile,
  skillLoadingSelector,
  updateCandidateProfile,
  updateExperience
} from '../../redux/features/candidate/candidateSlice';
import {
  currentFileSelector,
  deleteFile,
  fileFetchedSelector,
  findFileByUserId,
  isProcessingSelector,
  uploadFile
} from '../../redux/features/file/filesSlice';
import {
  ExperienceDialog,
  ProfilePhotoUpload,
  SkillDialog
} from './components';
import { useCurrentUserInfo } from '../../hooks/useCurrentUserInfo';
import { useHistory } from 'react-router-dom';
import { APP_PATH_ONBOARDING } from '../../config';
import {
  trackCandidateOwnProfileView,
  trackProfileAboutEditStart,
  trackProfileAboutSaved,
  trackProfileExperienceDeleted,
  trackProfileExperienceDialogOpened,
  trackProfileExperienceSaved,
  trackProfileIntroEditStart,
  trackProfileIntroSaved,
  trackProfilePhotoDeleted,
  trackProfilePhotoUploaded,
  trackProfileSkillAdded,
  trackProfileSkillDeleted,
  trackProfileSkillDialogOpened
} from '../../helpers';

const useStyles = makeStyles((theme) => ({
  root: {
    backgroundColor: theme.palette.background.default,
    minHeight: '100vh',
    paddingBottom: theme.spacing(4)
  },
  // Hero Section (LinkedIn-style banner)
  heroSection: {
    position: 'relative',
    backgroundColor: theme.palette.background.paper,
    borderBottom: `1px solid ${theme.palette.divider}`,
    marginBottom: theme.spacing(3)
  },
  heroBanner: {
    height: 200,
    background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
    position: 'relative'
  },
  heroContent: {
    maxWidth: 1200,
    margin: '0 auto',
    padding: theme.spacing(0, 3),
    position: 'relative',
    marginTop: -80
  },
  profileAvatarSection: {
    display: 'flex',
    alignItems: 'flex-end',
    gap: theme.spacing(3),
    marginBottom: theme.spacing(2),
    [theme.breakpoints.down('sm')]: {
      flexDirection: 'column',
      alignItems: 'center',
      textAlign: 'center'
    }
  },
  profileAvatar: {
    width: 160,
    height: 160,
    border: `4px solid ${theme.palette.background.paper}`,
    boxShadow: theme.shadows[3],
    backgroundColor: theme.palette.background.paper,
    fontSize: '3rem',
    fontWeight: 700,
    color: theme.palette.primary.main
  },
  profileInfo: {
    flex: 1,
    paddingBottom: theme.spacing(2)
  },
  profileName: {
    fontWeight: 700,
    marginBottom: theme.spacing(0.5),
    color: theme.palette.text.primary
  },
  profileHeadline: {
    color: theme.palette.text.secondary,
    marginBottom: theme.spacing(1),
    fontSize: '1.125rem'
  },
  profileMeta: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(2),
    alignItems: 'center',
    color: theme.palette.text.secondary,
    marginTop: theme.spacing(1),
    [theme.breakpoints.down('sm')]: {
      justifyContent: 'center'
    }
  },
  profileMetaItem: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(0.5),
    fontSize: '0.875rem'
  },
  profileMetaIcon: {
    fontSize: '1rem',
    color: theme.palette.text.hint
  },
  editAvatarButton: {
    marginTop: theme.spacing(1),
    textTransform: 'none',
    fontSize: '0.875rem',
    fontWeight: 500
  },
  // Content Container
  contentContainer: {
    maxWidth: 1200,
    margin: '0 auto',
    padding: theme.spacing(0, 3)
  },
  // Section Cards
  sectionCard: {
    marginBottom: theme.spacing(3),
    borderRadius: theme.spacing(1),
    boxShadow: theme.shadows[1],
    '&:hover': {
      boxShadow: theme.shadows[2]
    }
  },
  sectionHeader: {
    padding: theme.spacing(3),
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    borderBottom: `1px solid ${theme.palette.divider}`
  },
  sectionTitle: {
    fontWeight: 600,
    fontSize: '1.25rem',
    color: theme.palette.text.primary
  },
  sectionContent: {
    padding: theme.spacing(3)
  },
  // About Section
  aboutText: {
    lineHeight: 1.7,
    whiteSpace: 'pre-wrap',
    color: theme.palette.text.primary
  },
  aboutTextCollapsed: {
    lineHeight: 1.7,
    whiteSpace: 'pre-wrap',
    color: theme.palette.text.primary,
    display: '-webkit-box',
    WebkitLineClamp: 3,
    WebkitBoxOrient: 'vertical',
    overflow: 'hidden'
  },
  seeMoreLink: {
    color: theme.palette.primary.main,
    cursor: 'pointer',
    fontWeight: 600,
    marginTop: theme.spacing(1),
    display: 'inline-block',
    '&:hover': {
      textDecoration: 'underline'
    }
  },
  // Experience Cards
  experienceItem: {
    display: 'flex',
    gap: theme.spacing(2),
    padding: theme.spacing(2, 0),
    borderBottom: `1px solid ${theme.palette.divider}`,
    '&:last-child': {
      borderBottom: 'none'
    }
  },
  experienceIcon: {
    width: 48,
    height: 48,
    backgroundColor: theme.palette.background.default,
    borderRadius: theme.spacing(1),
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0
  },
  experienceIconSvg: {
    fontSize: '1.5rem',
    color: theme.palette.primary.main
  },
  experienceContent: {
    flex: 1
  },
  experienceTitle: {
    fontWeight: 600,
    fontSize: '1rem',
    color: theme.palette.text.primary,
    marginBottom: theme.spacing(0.5)
  },
  experienceCompany: {
    color: theme.palette.text.secondary,
    fontSize: '0.875rem',
    marginBottom: theme.spacing(0.5)
  },
  experienceDate: {
    color: theme.palette.text.hint,
    fontSize: '0.8125rem',
    marginBottom: theme.spacing(1)
  },
  experienceDescription: {
    color: theme.palette.text.secondary,
    lineHeight: 1.6,
    whiteSpace: 'pre-wrap',
    fontSize: '0.875rem'
  },
  experienceActions: {
    display: 'flex',
    gap: theme.spacing(0.5),
    marginTop: theme.spacing(1)
  },
  // Skills
  skillsContainer: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(1)
  },
  skillChip: {
    fontWeight: 500,
    fontSize: '0.875rem',
    height: 36,
    '& .MuiChip-label': {
      padding: theme.spacing(0, 1.5)
    }
  },
  // Buttons
  editButton: {
    textTransform: 'none',
    fontWeight: 600
  },
  actionButton: {
    textTransform: 'none',
    fontWeight: 600,
    boxShadow: 'none',
    '&:hover': {
      boxShadow: theme.shadows[2]
    }
  },
  // Form
  form: {
    '& .MuiTextField-root': {
      marginBottom: theme.spacing(2)
    }
  },
  formActions: {
    display: 'flex',
    gap: theme.spacing(2),
    marginTop: theme.spacing(2)
  },
  // Empty States
  emptyState: {
    padding: theme.spacing(4),
    textAlign: 'center',
    color: theme.palette.text.secondary
  },
  emptyStateIcon: {
    fontSize: '3rem',
    marginBottom: theme.spacing(2),
    opacity: 0.5
  },
  // Loading
  loadingContainer: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    minHeight: 400,
    gap: theme.spacing(2)
  }
}));

const getUserInitials = (fullName) => {
  if (!fullName) return '?';
  const names = fullName.trim().split(' ');
  if (names.length >= 2) {
    return `${names[0][0]}${names[names.length - 1][0]}`.toUpperCase();
  }
  return fullName[0].toUpperCase();
};

const CandidateProfile = () => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const history = useHistory();
  const { user, userId } = useCurrentUserInfo();

  const profile = useSelector(candidateProfileSelector);
  const isLoading = useSelector(candidateLoadingSelector);
  const fetched = useSelector(candidateFetchedSelector);
  const experienceLoading = useSelector(experienceLoadingSelector);
  const skillLoading = useSelector(skillLoadingSelector);

  // File upload state
  const currentFile = useSelector(currentFileSelector);
  const isProcessingFile = useSelector(isProcessingSelector);
  const fileFetched = useSelector(fileFetchedSelector);

  const [isEditingIntro, setIsEditingIntro] = useState(false);
  const [isEditingAbout, setIsEditingAbout] = useState(false);
  const [showFullAbout, setShowFullAbout] = useState(false);
  const [introData, setIntroData] = useState({});
  const [aboutData, setAboutData] = useState('');
  const [photoDialogOpen, setPhotoDialogOpen] = useState(false);

  // Experience dialog state
  const [experienceDialogOpen, setExperienceDialogOpen] = useState(false);
  const [editingExperience, setEditingExperience] = useState(null);

  // Skill dialog state
  const [skillDialogOpen, setSkillDialogOpen] = useState(false);

  useEffect(() => {
    if (!fetched && !isLoading) {
      console.log('📋 [CandidateProfile] Fetching candidate profile');
      dispatch(fetchCandidateProfile());
    }
  }, [dispatch, fetched, isLoading]);

  // Fetch user's photo/file
  useEffect(() => {
    if (userId && !fileFetched) {
      console.log('📸 [CandidateProfile] Fetching user photo for:', userId);
      dispatch(findFileByUserId({ userId, isPublic: false }));
    }
  }, [dispatch, userId, fileFetched]);

  useEffect(() => {
    if (profile) {
      setIntroData({
        contactEmail: profile.contactEmail || '',
        linkedinUrl: profile.linkedinUrl || '',
        headline: profile.headline || '',
        currentPosition: profile.currentPosition || '',
        education: profile.education || '',
        location: profile.location || '',
        postalCode: profile.postalCode || '',
        city: profile.city || '',
        coverLetter: profile.coverLetter || ''
      });
      setAboutData(profile.about || '');
    }
  }, [profile]);

  // Track page view when profile is loaded
  useEffect(() => {
    if (profile && userId) {
      const hasPhoto = !!currentFile?.publicUrl;
      const hasExperience = (profile.experiences || []).length > 0;
      const hasSkills = (profile.skills || []).length > 0;
      const experienceCount = (profile.experiences || []).length;
      const skillCount = (profile.skills || []).length;

      trackCandidateOwnProfileView(
        userId,
        hasPhoto,
        hasExperience,
        hasSkills,
        experienceCount,
        skillCount
      );
    }
  }, [profile, userId, currentFile]);

  const handleSaveIntro = () => {
    // Track intro save with fields
    trackProfileIntroSaved(
      !!introData.headline,
      !!introData.currentPosition,
      !!introData.education,
      !!(introData.city || introData.location)
    );

    // Send only the intro fields
    dispatch(updateCandidateProfile(introData));
    setIsEditingIntro(false);
  };

  const handleCancelIntro = () => {
    if (profile) {
      setIntroData({
        contactEmail: profile.contactEmail || '',
        linkedinUrl: profile.linkedinUrl || '',
        headline: profile.headline || '',
        currentPosition: profile.currentPosition || '',
        education: profile.education || '',
        location: profile.location || '',
        postalCode: profile.postalCode || '',
        city: profile.city || '',
        coverLetter: profile.coverLetter || ''
      });
    }
    setIsEditingIntro(false);
  };

  const handleSaveAbout = () => {
    // Track about save with word count
    const wordCount = aboutData
      ? aboutData
          .trim()
          .split(/\s+/)
          .filter((word) => word.length > 0).length
      : 0;
    trackProfileAboutSaved(wordCount);

    // Send only the about field - backend will preserve other fields
    dispatch(updateCandidateProfile({ about: aboutData }));
    setIsEditingAbout(false);
  };

  const handleCancelAbout = () => {
    setAboutData(profile?.about || '');
    setIsEditingAbout(false);
  };

  // TODO: Implement experience management
  const handleAddExperience = () => {
    trackProfileExperienceDialogOpened(false);
    setEditingExperience(null);
    setExperienceDialogOpen(true);
  };

  const handleEditExperience = (experience) => {
    trackProfileExperienceDialogOpened(true);
    setEditingExperience(experience);
    setExperienceDialogOpen(true);
  };

  const handleSaveExperience = (experienceData) => {
    // Track experience save
    trackProfileExperienceSaved(
      !!editingExperience,
      !!experienceData.isCurrentRole,
      !!experienceData.description
    );

    if (editingExperience) {
      // Update existing experience
      dispatch(updateExperience(editingExperience.id, experienceData));
    } else {
      // Add new experience
      dispatch(addExperience(experienceData));
    }
    setExperienceDialogOpen(false);
    setEditingExperience(null);
  };

  const handleDeleteExperience = (experienceId) => {
    if (window.confirm('Are you sure you want to delete this experience?')) {
      trackProfileExperienceDeleted();
      dispatch(deleteExperience(experienceId));
    }
  };

  // TODO: Implement skills management
  const handleAddSkill = () => {
    trackProfileSkillDialogOpened();
    setSkillDialogOpen(true);
  };

  const handleSaveSkill = (skillData) => {
    trackProfileSkillAdded(skillData.skillName || '');
    dispatch(addSkill(skillData));
    setSkillDialogOpen(false);
  };

  const handleDeleteSkill = (skillId) => {
    if (window.confirm('Are you sure you want to delete this skill?')) {
      // Find skill name for tracking
      const skill = profile?.skills?.find((s) => s.id === skillId);
      trackProfileSkillDeleted(skill?.skillName || '');
      dispatch(deleteSkill(skillId));
    }
  };

  // Photo upload handlers
  const handlePhotoUpload = (file) => {
    console.log('📸 [CandidateProfile] Uploading photo:', file.name);
    trackProfilePhotoUploaded(file.type, file.size);
    dispatch(uploadFile({ file }));
  };

  const handlePhotoDelete = () => {
    if (currentFile && currentFile.id) {
      if (
        window.confirm('Are you sure you want to delete your profile photo?')
      ) {
        console.log('🗑️ [CandidateProfile] Deleting photo:', currentFile.id);
        trackProfilePhotoDeleted();
        dispatch(deleteFile({ fileId: currentFile.id }));
      }
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const [year, month] = dateString.split('-');
    const monthNames = [
      'Jan',
      'Feb',
      'Mar',
      'Apr',
      'May',
      'Jun',
      'Jul',
      'Aug',
      'Sep',
      'Oct',
      'Nov',
      'Dec'
    ];
    return `${monthNames[parseInt(month) - 1]} ${year}`;
  };

  if (isLoading && !profile) {
    return (
      <Box className={classes.root}>
        <Box className={classes.loadingContainer}>
          <CircularProgress size={60} />
          <Typography variant="h6" color="textSecondary">
            Loading your profile...
          </Typography>
        </Box>
      </Box>
    );
  }

  const userName = user?.fullName || user?.full_name || user?.email || 'User';
  const userInitials = getUserInitials(userName);
  const photoUrl = currentFile?.publicUrl;

  return (
    <Box className={classes.root}>
      {/* Hero Section - LinkedIn Style Banner */}
      <Box className={classes.heroSection}>
        <Box className={classes.heroBanner} />
        <Box className={classes.heroContent}>
          <Box className={classes.profileAvatarSection}>
            <Box>
              <Avatar src={photoUrl} className={classes.profileAvatar}>
                {!photoUrl && userInitials}
              </Avatar>
              <Button
                className={classes.editAvatarButton}
                size="small"
                startIcon={<EditIcon />}
                onClick={() => setPhotoDialogOpen(true)}
                disabled={isProcessingFile}>
                {isProcessingFile ? 'Uploading...' : 'Edit photo'}
              </Button>
            </Box>
            <Box className={classes.profileInfo}>
              <Typography variant="h3" className={classes.profileName}>
                {userName}
              </Typography>
              <Typography variant="h6" className={classes.profileHeadline}>
                {profile?.headline || 'Add a headline to stand out'}
              </Typography>
              <Box className={classes.profileMeta}>
                {profile?.currentPosition && (
                  <Box className={classes.profileMetaItem}>
                    <WorkIcon className={classes.profileMetaIcon} />
                    <span>{profile.currentPosition}</span>
                  </Box>
                )}
                {profile?.education && (
                  <Box className={classes.profileMetaItem}>
                    <SchoolIcon className={classes.profileMetaIcon} />
                    <span>{profile.education}</span>
                  </Box>
                )}
                {(profile?.city || profile?.location) && (
                  <Box className={classes.profileMetaItem}>
                    <LocationOnIcon className={classes.profileMetaIcon} />
                    <span>
                      {[profile?.city, profile?.location]
                        .filter(Boolean)
                        .join(', ')}
                    </span>
                  </Box>
                )}
                {profile?.contactEmail && (
                  <Box className={classes.profileMetaItem}>
                    <EmailIcon className={classes.profileMetaIcon} />
                    <span>{profile.contactEmail}</span>
                  </Box>
                )}
                {profile?.linkedinUrl && (
                  <Box className={classes.profileMetaItem}>
                    <LinkedInIcon className={classes.profileMetaIcon} />
                    <a
                      href={profile.linkedinUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{ color: 'inherit', textDecoration: 'none' }}>
                      LinkedIn Profile
                    </a>
                  </Box>
                )}
              </Box>
            </Box>
            <Box>
              <Tooltip title="Complete your profile">
                <IconButton
                  color="primary"
                  onClick={() => {
                    trackProfileIntroEditStart();
                    history.push(APP_PATH_ONBOARDING);
                  }}>
                  <EditIcon />
                </IconButton>
              </Tooltip>
            </Box>
          </Box>
        </Box>
      </Box>

      {/* Main Content */}
      <Box className={classes.contentContainer}>
        {/* About Section */}
        <Card className={classes.sectionCard}>
          <Box className={classes.sectionHeader}>
            <Typography className={classes.sectionTitle}>About</Typography>
            {!isEditingAbout && (
              <IconButton
                size="small"
                onClick={() => {
                  trackProfileAboutEditStart();
                  setIsEditingAbout(true);
                }}>
                <EditIcon />
              </IconButton>
            )}
          </Box>
          <Box className={classes.sectionContent}>
            {!isEditingAbout ? (
              <Box>
                <Typography
                  className={
                    showFullAbout ||
                    !profile?.about ||
                    profile.about.length < 200
                      ? classes.aboutText
                      : classes.aboutTextCollapsed
                  }>
                  {profile?.about ||
                    'Share your professional journey, skills, and what drives you...'}
                </Typography>
                {profile?.about && profile.about.length >= 200 && (
                  <Typography
                    className={classes.seeMoreLink}
                    onClick={() => setShowFullAbout(!showFullAbout)}>
                    {showFullAbout ? '...see less' : '...see more'}
                  </Typography>
                )}
              </Box>
            ) : (
              <Box>
                <TextField
                  fullWidth
                  multiline
                  minRows={6}
                  placeholder="Write a summary about your professional background, skills, and goals..."
                  variant="outlined"
                  value={aboutData}
                  onChange={(e) => {
                    const newValue = e.target.value;
                    if (newValue.length <= 2600) {
                      setAboutData(newValue);
                    }
                  }}
                  helperText={`${aboutData.length.toLocaleString()}/${(2600).toLocaleString()} characters`}
                  error={aboutData.length >= 2600}
                />
                <Box className={classes.formActions}>
                  <Button
                    variant="contained"
                    color="primary"
                    className={classes.actionButton}
                    startIcon={<SaveIcon />}
                    onClick={handleSaveAbout}>
                    Save
                  </Button>
                  <Button
                    variant="outlined"
                    className={classes.actionButton}
                    startIcon={<CancelIcon />}
                    onClick={handleCancelAbout}>
                    Cancel
                  </Button>
                </Box>
              </Box>
            )}
          </Box>
        </Card>

        {/* Experience Section */}
        <Card className={classes.sectionCard}>
          <Box className={classes.sectionHeader}>
            <Typography className={classes.sectionTitle}>Experience</Typography>
            <Button
              className={classes.editButton}
              startIcon={<AddIcon />}
              color="primary"
              onClick={handleAddExperience}
              disabled={experienceLoading}>
              Add experience
            </Button>
          </Box>
          <Box className={classes.sectionContent}>
            {profile?.experiences && profile.experiences.length > 0 ? (
              profile.experiences.map((exp) => (
                <Box key={exp.id} className={classes.experienceItem}>
                  <Box className={classes.experienceIcon}>
                    <WorkIcon className={classes.experienceIconSvg} />
                  </Box>
                  <Box className={classes.experienceContent}>
                    <Typography className={classes.experienceTitle}>
                      {exp.title}
                    </Typography>
                    <Typography className={classes.experienceCompany}>
                      {exp.company}
                      {exp.employmentType && ` • ${exp.employmentType}`}
                    </Typography>
                    <Typography className={classes.experienceDate}>
                      {formatDate(exp.startDate)} -{' '}
                      {exp.isCurrentRole ? 'Present' : formatDate(exp.endDate)}
                      {exp.location && ` • ${exp.location}`}
                      {exp.locationType && ` • ${exp.locationType}`}
                    </Typography>
                    {exp.description && (
                      <Typography className={classes.experienceDescription}>
                        {exp.description}
                      </Typography>
                    )}
                    <Box className={classes.experienceActions}>
                      <IconButton
                        size="small"
                        onClick={() => handleEditExperience(exp)}
                        disabled={experienceLoading}>
                        <EditIcon fontSize="small" />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => handleDeleteExperience(exp.id)}
                        disabled={experienceLoading}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  </Box>
                </Box>
              ))
            ) : (
              <Box className={classes.emptyState}>
                <WorkIcon className={classes.emptyStateIcon} />
                <Typography variant="body1" color="textSecondary">
                  No experience added yet
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  Share your work history to showcase your professional journey
                </Typography>
              </Box>
            )}
          </Box>
        </Card>

        {/* Skills Section */}
        <Card className={classes.sectionCard}>
          <Box className={classes.sectionHeader}>
            <Typography className={classes.sectionTitle}>Skills</Typography>
            <Button
              className={classes.editButton}
              startIcon={<AddIcon />}
              color="primary"
              onClick={handleAddSkill}
              disabled={skillLoading}>
              Add skill
            </Button>
          </Box>
          <Box className={classes.sectionContent}>
            {profile?.skills && profile.skills.length > 0 ? (
              <Box className={classes.skillsContainer}>
                {profile.skills.map((skill) => (
                  <Chip
                    key={skill.id}
                    label={skill.skillName}
                    onDelete={() => handleDeleteSkill(skill.id)}
                    className={classes.skillChip}
                    color="primary"
                    variant="outlined"
                    disabled={skillLoading}
                  />
                ))}
              </Box>
            ) : (
              <Box className={classes.emptyState}>
                <Typography variant="body1" color="textSecondary">
                  No skills added yet
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  Add skills to highlight your expertise
                </Typography>
              </Box>
            )}
          </Box>
        </Card>
      </Box>

      {/* Dialogs */}

      {/* Edit Intro Dialog */}
      <Dialog
        open={isEditingIntro}
        onClose={handleCancelIntro}
        maxWidth="md"
        fullWidth>
        <DialogTitle>Edit Intro</DialogTitle>
        <DialogContent>
          <Box className={classes.form}>
            <TextField
              fullWidth
              label="Headline"
              placeholder="e.g., Senior Software Engineer | Full-Stack Developer"
              variant="outlined"
              value={introData.headline}
              onChange={(e) =>
                setIntroData({ ...introData, headline: e.target.value })
              }
            />
            <TextField
              fullWidth
              label="Current Position / Last Position"
              placeholder="e.g., Senior Developer at Tech Company"
              variant="outlined"
              value={introData.currentPosition}
              onChange={(e) =>
                setIntroData({
                  ...introData,
                  currentPosition: e.target.value
                })
              }
            />
            <TextField
              fullWidth
              label="Education"
              placeholder="e.g., BS Computer Science, MIT"
              variant="outlined"
              value={introData.education}
              onChange={(e) =>
                setIntroData({ ...introData, education: e.target.value })
              }
            />
            <Grid container spacing={2}>
              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  label="City"
                  placeholder="e.g., San Francisco"
                  variant="outlined"
                  value={introData.city}
                  onChange={(e) =>
                    setIntroData({ ...introData, city: e.target.value })
                  }
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  label="Postal Code"
                  placeholder="e.g., 94102"
                  variant="outlined"
                  value={introData.postalCode}
                  onChange={(e) =>
                    setIntroData({ ...introData, postalCode: e.target.value })
                  }
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  label="Location (Country/State)"
                  placeholder="e.g., CA, USA"
                  variant="outlined"
                  value={introData.location}
                  onChange={(e) =>
                    setIntroData({ ...introData, location: e.target.value })
                  }
                />
              </Grid>
            </Grid>
            <TextField
              fullWidth
              label="Contact Email"
              type="email"
              placeholder="your.email@example.com"
              variant="outlined"
              value={introData.contactEmail}
              onChange={(e) =>
                setIntroData({ ...introData, contactEmail: e.target.value })
              }
            />
            <TextField
              fullWidth
              label="LinkedIn URL"
              placeholder="https://linkedin.com/in/yourprofile"
              variant="outlined"
              value={introData.linkedinUrl}
              onChange={(e) =>
                setIntroData({ ...introData, linkedinUrl: e.target.value })
              }
            />
            <TextField
              fullWidth
              label="Cover Letter Template (Default for applications)"
              multiline
              minRows={4}
              placeholder="Write a default cover letter that will be used when you apply to jobs..."
              variant="outlined"
              value={introData.coverLetter}
              onChange={(e) =>
                setIntroData({ ...introData, coverLetter: e.target.value })
              }
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelIntro} className={classes.actionButton}>
            Cancel
          </Button>
          <Button
            onClick={handleSaveIntro}
            color="primary"
            variant="contained"
            className={classes.actionButton}
            startIcon={<SaveIcon />}>
            Save Changes
          </Button>
        </DialogActions>
      </Dialog>

      <ExperienceDialog
        open={experienceDialogOpen}
        onClose={() => {
          setExperienceDialogOpen(false);
          setEditingExperience(null);
        }}
        onSave={handleSaveExperience}
        experience={editingExperience}
        loading={experienceLoading}
      />

      <SkillDialog
        open={skillDialogOpen}
        onClose={() => setSkillDialogOpen(false)}
        onSave={handleSaveSkill}
        loading={skillLoading}
      />

      {/* Photo Upload Dialog */}
      <Dialog
        open={photoDialogOpen}
        onClose={() => !isProcessingFile && setPhotoDialogOpen(false)}
        maxWidth="sm"
        fullWidth>
        <DialogTitle>Edit Profile Photo</DialogTitle>
        <DialogContent>
          <ProfilePhotoUpload
            photoUrl={photoUrl}
            userName={userName}
            onUpload={(file) => {
              handlePhotoUpload(file);
              setPhotoDialogOpen(false);
            }}
            onDelete={() => {
              handlePhotoDelete();
              setPhotoDialogOpen(false);
            }}
            isProcessing={isProcessingFile}
          />
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setPhotoDialogOpen(false)}
            disabled={isProcessingFile}>
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default CandidateProfile;
